import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import Header from "./components/Header";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import Destinations from "./pages/Destinations";
import Packages from "./pages/Packages";
import Booking from "./pages/Booking";
import Contact from "./pages/Contact";
import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import MyBookings from "./pages/MyBookings";
import PackageDetail from "./pages/PackageDetail";
import Admin from "./pages/Admin";
import { ForgotPassword, ResetPassword } from "./pages/PasswordReset";
import { WhatsAppButton } from "./components/WhatsAppButton";
import Flights from "./pages/Flights";
import Hotels from "./pages/Hotels";
import Profile from "./pages/Profile";
import { CurrencyProvider } from "./context/CurrencyContext";
import { LanguageProvider } from "./context/LanguageContext";
import { BrandingProvider } from "./context/BrandingContext";
import PaymentResult from "./pages/PaymentResult";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  // Check auth status
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
          withCredentials: true
        });
        setUser(response.data);
      } catch (error) {
        setUser(null);
      } finally {
        setAuthChecked(true);
      }
    };
    checkAuth();
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, {
        withCredentials: true
      });
      setUser(null);
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Check for session_id in URL hash - render only AuthCallback
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Hide header and footer on auth pages
  const hideHeaderFooter = ['/login', '/forgot-password', '/reset-password'].includes(location.pathname);

  return (
    <>
      {!hideHeaderFooter && <Header user={user} onLogout={handleLogout} />}
      <Routes>
        <Route path="/" element={<Home user={user} />} />
        <Route path="/destinations" element={<Destinations user={user} />} />
        <Route path="/packages" element={<Packages user={user} />} />
        <Route path="/packages/:id" element={<PackageDetail user={user} />} />
        <Route path="/booking" element={<Booking user={user} authChecked={authChecked} />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/login" element={<Login onAuth={setUser} />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/my-bookings" element={<MyBookings />} />
        <Route path="/admin" element={<Admin user={user} authChecked={authChecked} />} />
        <Route path="/flights" element={<Flights user={user} authChecked={authChecked} />} />
        <Route path="/hotels" element={<Hotels user={user} authChecked={authChecked} />} />
        <Route path="/payment-result" element={<PaymentResult />} />
        <Route path="/profile" element={<Profile user={user} authChecked={authChecked} onUserUpdate={setUser} />} />
      </Routes>
      {!hideHeaderFooter && <Footer />}
      {!hideHeaderFooter && <WhatsAppButton />}
      <Toaster />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <LanguageProvider>
          <BrandingProvider>
            <CurrencyProvider>
              <AppContent />
            </CurrencyProvider>
          </BrandingProvider>
        </LanguageProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
